import { createSlice } from '@reduxjs/toolkit';
import { callApi } from '../../utils/apiCall';
import { dispatch } from '../store';

// ---------------------------------------------------------------------------
// Instagram DM Inbox — Phase 1 (read-only)
// ---------------------------------------------------------------------------

const initialState = {
    // loading flags
    accountsLoading: false,
    threadsLoading: false,
    messagesLoading: false,
    // data
    accounts: [],
    threads: [],
    messages: [],
    // selection
    selectedThread: null,
    selectedAccountId: null,
    // error
    error: null,
};

const slice = createSlice({
    name: 'instagramInbox',
    initialState,
    reducers: {
        startAccountsLoad(state) {
            state.accountsLoading = true;
            state.error = null;
        },
        completeAccountsLoad(state, action) {
            state.accountsLoading = false;
            state.accounts = action.payload ?? [];
        },
        startThreadsLoad(state) {
            state.threadsLoading = true;
            state.error = null;
        },
        completeThreadsLoad(state, action) {
            state.threadsLoading = false;
            state.threads = action.payload ?? [];
        },
        startMessagesLoad(state) {
            state.messagesLoading = true;
            state.error = null;
        },
        completeMessagesLoad(state, action) {
            state.messagesLoading = false;
            state.messages = action.payload ?? [];
        },
        setSelectedThread(state, action) {
            state.selectedThread = action.payload;
            state.messages = [];
        },
        setSelectedAccountId(state, action) {
            state.selectedAccountId = action.payload;
            state.threads = [];
            state.selectedThread = null;
            state.messages = [];
        },
        setError(state, action) {
            state.error = action.payload;
            state.accountsLoading = false;
            state.threadsLoading = false;
            state.messagesLoading = false;
        },
        reset() {
            return initialState;
        },
    },
});

export default slice.reducer;

// ---------------------------------------------------------------------------
// Thunks
// ---------------------------------------------------------------------------

export function getInstagramAccounts(practiceId) {
    return async () => {
        try {
            dispatch(slice.actions.startAccountsLoad());
            const response = await callApi(`/api/v1/practices/${practiceId}/instagram-accounts/`);
            const data = response?.data;
            // Backend may return a list directly or a paginated {results: [...]}
            const accounts = Array.isArray(data) ? data : (data?.results ?? []);
            dispatch(slice.actions.completeAccountsLoad(accounts));
        } catch (err) {
            dispatch(slice.actions.setError(err?.message ?? 'Failed to load Instagram accounts'));
        }
    };
}

export function getInstagramThreads(practiceId, accountId) {
    return async () => {
        try {
            dispatch(slice.actions.startThreadsLoad());
            const response = await callApi(
                `/api/v1/practices/${practiceId}/instagram-accounts/${accountId}/threads/`
            );
            const data = response?.data;
            const threads = Array.isArray(data) ? data : (data?.results ?? []);
            dispatch(slice.actions.completeThreadsLoad(threads));
        } catch (err) {
            dispatch(slice.actions.setError(err?.message ?? 'Failed to load Instagram threads'));
        }
    };
}

export function getInstagramMessages(practiceId, accountId, threadId) {
    return async () => {
        try {
            dispatch(slice.actions.startMessagesLoad());
            const response = await callApi(
                `/api/v1/practices/${practiceId}/instagram-accounts/${accountId}/threads/${threadId}/messages/`
            );
            const data = response?.data;
            const messages = Array.isArray(data) ? data : (data?.results ?? []);
            dispatch(slice.actions.completeMessagesLoad(messages));
        } catch (err) {
            dispatch(slice.actions.setError(err?.message ?? 'Failed to load Instagram messages'));
        }
    };
}

// Named action exports for external use
export const { setSelectedThread, setSelectedAccountId, reset: resetInstagramInbox } = slice.actions;
